import { useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useWalletStore } from '@/stores/useWalletStore'
import { useLendStore } from '@/stores/useLendStore'
import { useEosTransaction } from '@/hooks/useEosTransaction'
import { getCoinIcon } from '@/lib/common'
import { CommonFooter } from '@/components/layout'
import { toFixed, decdiv } from '@/lib/decimal'
import { handleFormatUserInfo, handleGetHealthFactors } from '@/lib/lend-data'
import noDataImg from '@/assets/images/noData.svg'

export function LendAssetsPage() {
  const { t } = useTranslation()
  const { isLogin, session } = useWalletStore()
  const { marketList, fetchMarketList, fetchLendFarmApr, fetchWalletBalance, fetchUserLendData } = useLendStore()
  const { transact, loading: txLoading } = useEosTransaction()
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const accountName = session?.account?.name

  useEffect(() => {
    if (!accountName) return
    const init = async () => {
      await fetchMarketList()
      await fetchLendFarmApr()
      await fetchWalletBalance(accountName)
      await fetchUserLendData(accountName)
    }
    init()
    timerRef.current = setInterval(init, 10000)
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [accountName])

  const stats = useMemo(() => {
    const info = handleFormatUserInfo(marketList)
    const healthFactor = handleGetHealthFactors(marketList, 1)
    return { ...info, healthFactor }
  }, [marketList])

  const deposits = useMemo(() => marketList.filter((item: any) => parseFloat(item.depositBalance) > 0), [marketList])
  const borrows = useMemo(() => marketList.filter((item: any) => parseFloat(item.borrowCompound) > 0), [marketList])

  const handleToggleCollateral = async (item: any) => {
    if (!session) return
    const account = session.account
    const actions = [{
      account: 'lending.ttr',
      name: 'useascollat',
      authorization: [{ actor: account.name, permission: account.authority || 'active' }],
      data: {
        user: account.name,
        reserve_id: item.id,
        use_as_collateral: item.use_as_collateral ? 0 : 1,
      },
    }]
    const result = await transact(actions, t('swap.success'))
    if (result && accountName) {
      await fetchUserLendData(accountName)
    }
  }

  if (!isLogin) {
    return (
      <div className="w-full flex justify-center px-4 md:px-[32px] md:mt-[32px]">
        <div className="w-full max-w-[600px]">
          <div className="bg-white rounded-[26px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] px-[16px] py-[24px]">
            <div className="flex flex-col items-center py-[30px]">
              <img src={noDataImg} className="w-[120px] mb-[12px]" alt="" />
              <span className="text-[14px] text-[#999]">{t('public.noauth')}</span>
            </div>
          </div>
          <CommonFooter />
        </div>
      </div>
    )
  }

  return (
    <div className="w-full flex justify-center px-4 md:px-[32px] md:mt-[32px]">
      <div className="w-full max-w-[600px]">
        {/* Stats board */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-[8px] mb-[16px]">
          {[
            { label: t('lend.myDeposit'), value: stats.totalDeposits > 0 ? '$' + toFixed(stats.totalDeposits, 2) : '--' },
            { label: t('lend.myBorrow'), value: stats.totalLoans > 0 ? '$' + toFixed(stats.totalLoans, 2) : '--' },
            { label: 'Health Factor', value: stats.healthFactor > 0 ? String(stats.healthFactor) : '--' },
            { label: 'Daily Earnings', value: stats.everydayEarn > 0 ? '$' + toFixed(stats.everydayEarn, 4) : '--' },
          ].map((item) => (
            <div key={item.label} className="bg-white rounded-[16px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] p-[12px]">
              <p className="text-[12px] text-[#999] mb-[4px]">{item.label}</p>
              <p className="text-[14px] font-bold text-foreground">{item.value}</p>
            </div>
          ))}
        </div>

        {/* My Deposits */}
        <div className="bg-white rounded-[26px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] px-[16px] py-[24px] mb-[16px]">
          <h3 className="text-[16px] font-bold text-foreground mb-[16px]">{t('lend.myDeposit')}</h3>

          {deposits.length === 0 ? (
            <div className="flex flex-col items-center py-[20px]">
              <img src={noDataImg} className="w-[80px] mb-[8px]" alt="" />
              <span className="text-[13px] text-[#999]">{t('public.noData')}</span>
            </div>
          ) : (
            <div className="space-y-[10px]">
              {deposits.map((item: any) => {
                const depositRate = item.current_liquidity_rate ? parseFloat(decdiv(item.current_liquidity_rate, 1e14)) * 100 : 0
                const miningApr = (item.apr || 0) * 100

                return (
                  <div key={item.id} className="p-[12px] rounded-[16px] border border-[#e5e5e5]">
                    <div className="flex items-center justify-between mb-[8px]">
                      <div className="flex items-center gap-[8px]">
                        <img src={getCoinIcon(item.contract, item.symbol)} className="w-[24px] h-[24px] rounded-full bg-gray-100" alt="" />
                        <span className="text-[14px] font-semibold">{item.symbol}</span>
                      </div>
                      <button
                        onClick={() => handleToggleCollateral(item)}
                        disabled={txLoading}
                        className={`text-[12px] px-[10px] py-[3px] rounded-full cursor-pointer ${item.use_as_collateral ? 'bg-[#e8f5e9] text-[#498846]' : 'bg-gray-100 text-[#999]'}`}
                      >
                        {item.use_as_collateral ? 'Collateral' : 'Not Collateral'}
                      </button>
                    </div>
                    <div className="space-y-[4px] text-[13px]">
                      <div className="flex justify-between">
                        <span className="text-[#666]">{t('public.balance')}</span>
                        <span>{toFixed(parseFloat(item.depositBalance), item.decimal)} {item.symbol}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#666]">{t('lend.depositApy')}</span>
                        <span className="text-[#498846]">{toFixed(depositRate + miningApr, 2)}%</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* My Borrows */}
        <div className="bg-white rounded-[26px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] px-[16px] py-[24px]">
          <h3 className="text-[16px] font-bold text-foreground mb-[16px]">{t('lend.myBorrow')}</h3>

          {borrows.length === 0 ? (
            <div className="flex flex-col items-center py-[20px]">
              <img src={noDataImg} className="w-[80px] mb-[8px]" alt="" />
              <span className="text-[13px] text-[#999]">{t('public.noData')}</span>
            </div>
          ) : (
            <div className="space-y-[10px]">
              {borrows.map((item: any) => {
                const borrowRate = item.current_variable_borrow_rate ? parseFloat(decdiv(item.current_variable_borrow_rate, 1e14)) * 100 : 0

                return (
                  <div key={item.id} className="p-[12px] rounded-[16px] border border-[#e5e5e5]">
                    <div className="flex items-center gap-[8px] mb-[8px]">
                      <img src={getCoinIcon(item.contract, item.symbol)} className="w-[24px] h-[24px] rounded-full bg-gray-100" alt="" />
                      <span className="text-[14px] font-semibold">{item.symbol}</span>
                    </div>
                    <div className="space-y-[4px] text-[13px]">
                      <div className="flex justify-between">
                        <span className="text-[#666]">{t('lend.borrow')}</span>
                        <span>{toFixed(parseFloat(item.borrowCompound), item.decimal)} {item.symbol}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#666]">{t('lend.borrowApy')}</span>
                        <span className="text-[#c0392b]">{toFixed(borrowRate, 2)}%</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <CommonFooter />
      </div>
    </div>
  )
}
